/**
 * Local execution-context builder — produces the exact `context.json` + `VERENTIS_*` env the
 * platform injects into engine containers (contract: Verentis.Execution ExecutionProtocol /
 * ExecutionContext, protocol version 1). The engine SDK auto-loads either form.
 */
import { randomUUID } from 'node:crypto'
import { decodeJwtPayload } from '../jwt.js'

export interface LocalContextInput {
  apiUrl: string
  accessToken: string
  scopes: string[]
  workspaceId: string
  workspaceName?: string
  filePath?: string
  fileMimeType?: string
  branch?: string
  tool?: string
  mode?: string
  timeoutSeconds?: number
  args?: string[]
  env?: Record<string, string>
  parameters?: Record<string, unknown>
  executionId?: string
}

export interface LocalExecutionContext {
  version: 1
  execution: { id: string; mode: string; tool?: string; trigger: string; timeout: number }
  workspace: { id: string; name?: string }
  file?: { path: string; name: string; mimeType?: string; branch: string }
  input: { arguments: string[]; environment: Record<string, string>; parameters?: Record<string, unknown> }
  platform: { apiUrl: string; token: { accessToken: string; expiresAt?: string; scopes: string[] } }
}

export function buildLocalContext(input: LocalContextInput): LocalExecutionContext {
  const payload = decodeJwtPayload(input.accessToken)
  const expiresAt = payload?.exp ? new Date(payload.exp * 1000).toISOString() : undefined
  const filePath = input.filePath ? (input.filePath.startsWith('/') ? input.filePath : `/${input.filePath}`) : undefined

  return {
    version: 1,
    execution: {
      id: input.executionId ?? `local-${randomUUID()}`,
      mode: input.mode ?? 'request-response',
      ...(input.tool ? { tool: input.tool } : {}),
      trigger: 'user',
      timeout: input.timeoutSeconds ?? 300,
    },
    workspace: { id: input.workspaceId, ...(input.workspaceName ? { name: input.workspaceName } : {}) },
    ...(filePath
      ? {
          file: {
            path: filePath,
            name: filePath.split('/').pop() ?? filePath,
            ...(input.fileMimeType ? { mimeType: input.fileMimeType } : {}),
            branch: input.branch ?? 'main',
          },
        }
      : {}),
    input: {
      arguments: input.args ?? [],
      environment: input.env ?? {},
      ...(input.parameters ? { parameters: input.parameters } : {}),
    },
    platform: {
      apiUrl: input.apiUrl,
      token: { accessToken: input.accessToken, ...(expiresAt ? { expiresAt } : {}), scopes: input.scopes },
    },
  }
}

/** The env-var mirror of the context (ExecutionProtocol.EnvVars). */
export function contextEnv(context: LocalExecutionContext, contextPath: string): Record<string, string> {
  return {
    VERENTIS_CONTEXT_PATH: contextPath,
    VERENTIS_EXECUTION_ID: context.execution.id,
    VERENTIS_EXECUTION_MODE: context.execution.mode,
    ...(context.execution.tool ? { VERENTIS_EXECUTION_TOOL: context.execution.tool } : {}),
    VERENTIS_API_URL: context.platform.apiUrl,
    VERENTIS_ACCESS_TOKEN: context.platform.token.accessToken,
    VERENTIS_WORKSPACE_ID: context.workspace.id,
    ...(context.file
      ? {
          VERENTIS_FILE_PATH: context.file.path,
          ...(context.file.mimeType ? { VERENTIS_FILE_MIME_TYPE: context.file.mimeType } : {}),
          VERENTIS_FILE_BRANCH: context.file.branch,
        }
      : {}),
    VERENTIS_TIMEOUT: String(context.execution.timeout),
  }
}
